import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles,
  ArrowRight,
  Mail,
  CheckCircle2,
  ShieldAlert,
  Zap,
  Database,
  Users,
} from "lucide-react";
import Layout from "../layout/Layout";
import subExpired from "../assets/svg/sub-expired.svg";

const SubscriptionExpired = () => {
  const navigate = useNavigate();

  return (
    <Layout>
      <div className="tw-min-h-[calc(100vh-64px)] tw-w-full tw-flex tw-items-center tw-justify-center tw-bg-slate-900/30 tw-backdrop-blur-sm tw-p-4 sm:tw-p-6 !tw-font-inter">
        {/* Modal Card */}
        <div className="tw-max-w-xl tw-w-full tw-bg-white tw-rounded-3xl tw-shadow-[0_25px_60px_-15px_rgba(0,0,0,0.2)] tw-border tw-border-solid tw-border-slate-100 tw-overflow-hidden tw-relative tw-flex tw-flex-col">
          {/* Top Decorative Gradient Accent Bar */}
          <div className="tw-h-1.5 tw-w-full tw-bg-gradient-to-r tw-from-[#5D44FF] tw-via-indigo-500 tw-to-emerald-400" />

          {/* Hero Banner Section */}
          <div className="tw-p-6 sm:tw-p-8 tw-bg-gradient-to-b tw-from-indigo-50/60 tw-via-slate-50/40 tw-to-white tw-flex tw-flex-col tw-items-center tw-text-center tw-relative">
            {/* Status Pill Badge */}
            <div className="tw-inline-flex tw-items-center tw-gap-2 tw-px-3.5 tw-py-1.5 tw-rounded-full tw-bg-rose-500/10 tw-border tw-border-solid tw-border-rose-500/20 tw-text-rose-600 tw-text-xs tw-font-bold tw-mb-4">
              <ShieldAlert className="tw-w-3.5 tw-h-3.5 tw-text-rose-500" />
              <span>Subscription Expired</span>
            </div>

            {/* Illustration */}
            <div className="tw-relative tw-mb-3 tw-flex tw-items-center tw-justify-center">
              <div className="tw-absolute tw-w-28 tw-h-28 tw-bg-[#5D44FF]/10 tw-rounded-full tw-blur-xl tw-pointer-events-none" />
              <img
                src={subExpired}
                alt="Subscription Expired"
                className="tw-w-44 sm:tw-w-48 tw-h-auto tw-object-contain tw-relative tw-z-10 tw-drop-shadow-md"
              />
            </div>

            {/* Headline */}
            <h1 className="tw-text-2xl sm:tw-text-3xl tw-font-extrabold tw-text-slate-900 tw-tracking-tight tw-mb-2">
              Your Account Has Expired!
            </h1>

            {/* Subtitle */}
            <p className="tw-text-slate-600 tw-text-sm sm:tw-text-base tw-font-normal tw-leading-relaxed tw-max-w-md tw-mb-6">
              Renew your subscription now to regain instant access to fresh leads, verified contacts, and sales growth tools.
            </p>

            {/* Feature Highlights Pills */}
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-2.5 sm:tw-gap-4 tw-py-2.5 tw-px-4 tw-rounded-2xl tw-bg-white tw-border tw-border-solid tw-border-slate-200/80 tw-shadow-xs tw-mb-6 tw-flex-wrap">
              <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-slate-700">
                <Database className="tw-w-3.5 tw-h-3.5 tw-text-[#5D44FF]" />
                Unrestricted Search
              </span>
              <span className="tw-text-slate-300">•</span>
              <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-slate-700">
                <Users className="tw-w-3.5 tw-h-3.5 tw-text-[#5D44FF]" />
                Verified Contacts
              </span>
              <span className="tw-text-slate-300">•</span>
              <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-slate-700">
                <Zap className="tw-w-3.5 tw-h-3.5 tw-text-[#5D44FF]" />
                AI Outreach
              </span>
            </div>

            {/* CTA Button Block */}
            <div className="tw-w-full tw-max-w-md tw-flex tw-flex-col tw-gap-2.5">
              <button
                type="button"
                onClick={() => navigate("/checkout")}
                className="tw-group tw-relative tw-w-full tw-h-[52px] tw-px-6 tw-rounded-2xl tw-bg-gradient-to-r tw-from-[#5D44FF] tw-to-[#4F36F2] hover:tw-from-[#4F36F2] hover:tw-to-[#4329e4] tw-text-white tw-font-bold tw-text-base tw-tracking-wide tw-shadow-[0_10px_25px_-5px_rgba(93,68,255,0.4)] hover:tw-shadow-[0_15px_30px_-5px_rgba(93,68,255,0.55)] hover:tw-scale-[1.01] active:tw-scale-[0.99] tw-transition-all tw-duration-200 tw-cursor-pointer tw-flex tw-items-center tw-justify-between tw-border-0 tw-outline-none tw-overflow-hidden"
              >
                {/* Top Reflection Highlight */}
                <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-b tw-from-white/20 tw-to-transparent tw-pointer-events-none" />

                <div className="tw-flex tw-items-center tw-gap-2.5 tw-relative tw-z-10">
                  <Sparkles className="tw-w-5 tw-h-5 tw-text-white/90" />
                  <span>Update Now</span>
                </div>

                <div className="tw-w-8 tw-h-8 tw-rounded-xl tw-bg-white/15 tw-flex tw-items-center tw-justify-center tw-relative tw-z-10 group-hover:tw-bg-white/25 group-hover:tw-translate-x-1 tw-transition-all">
                  <ArrowRight className="tw-w-4 tw-h-4 tw-text-white" />
                </div>
              </button>

              <span className="tw-text-[11px] tw-font-medium tw-text-slate-500 tw-flex tw-items-center tw-justify-center tw-gap-1.5">
                <CheckCircle2 className="tw-w-3.5 tw-h-3.5 tw-text-emerald-500" />
                Instant activation — get back to generating leads in seconds!
              </span>
            </div>
          </div>

          {/* Card Footer */}
          <div className="tw-px-6 tw-py-4 tw-bg-slate-50/80 tw-border-t tw-border-solid tw-border-slate-100 tw-flex tw-items-center tw-justify-between tw-text-xs tw-text-slate-500 tw-flex-wrap tw-gap-2">
            <span>Need help choosing a plan?</span>
            <a
              href="mailto:contact@beyondleadz.ai"
              className="tw-text-[#5D44FF] tw-font-semibold hover:tw-underline tw-flex tw-items-center tw-gap-1.5"
            >
              <Mail className="tw-w-3.5 tw-h-3.5" />
              contact@beyondleadz.ai
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SubscriptionExpired;
